import { Link } from 'react-router-dom';

export default function DonabedianModelPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
            医療品質評価の標準フレームワーク
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            服薬指導の質は「センス」ではなく<br />「設計」で決まる
          </h1>
          <p className="text-2xl text-emerald-50 font-medium">
            良い指導が再現されない本当の理由
          </p>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <i className="ri-lightbulb-flash-line text-3xl"></i>
              結論
            </h2>
            <p className="text-lg leading-relaxed">
              服薬指導の質は、個々の薬剤師の経験や話し方では決まりません。<strong>「構造 → 過程 → 結果」の順で設計されているかどうか</strong>で決まります。
            </p>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            理論の解説｜ドナベディアン・モデルとは何か
          </h2>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              ドナベディアン・モデルは、医療の質を評価するために提唱されたフレームワークです。
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              提唱者は、医療品質研究の第一人者<strong className="text-emerald-600">アヴェディス・ドナベディアン</strong>。
            </p>
          </div>

          {/* Three Layers */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-10 rounded-2xl mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              医療の質を捉える3層
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-emerald-500">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-4-line text-2xl text-emerald-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Structure</h4>
                <p className="text-gray-600 font-medium">構造</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-teal-500">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-flow-chart text-2xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Process</h4>
                <p className="text-gray-600 font-medium">過程</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-cyan-500">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-trophy-line text-2xl text-cyan-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Outcome</h4>
                <p className="text-gray-600 font-medium">結果</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-8 rounded-2xl shadow-lg text-center">
            <p className="text-2xl font-bold">
              最大のポイント
            </p>
            <p className="text-xl mt-4">
              結果（Outcome）だけを見ても、質は改善しない。
            </p>
          </div>
        </div>
      </section>

      {/* Why Result-Only Evaluation Fails */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            なぜ「結果評価」だけでは失敗するのか
          </h2>

          <div className="bg-white p-8 rounded-2xl shadow-lg mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              多くの薬局で行われがちな評価
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-500 mt-1"></i>
                <p className="text-lg text-gray-700">「服薬継続率が上がった／下がった」</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-500 mt-1"></i>
                <p className="text-lg text-gray-700">「残薬が減った／減らない」</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-500 mt-1"></i>
                <p className="text-lg text-gray-700">「満足度が高い／低い」</p>
              </div>
            </div>
            <p className="text-lg text-gray-700 mt-6">
              しかし、これらはすべて<strong className="text-red-600">結果論</strong>です。
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-2xl mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              結果は、次の要因に大きく左右されます
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">症例ミックス</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">患者背景</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">処方内容</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">生活環境</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 rounded-xl shadow-lg">
            <p className="text-xl font-bold flex items-center gap-3">
              <i className="ri-arrow-right-line text-2xl"></i>
              結果だけを追うと、「なぜ良かった／悪かったのか」が分からない。
            </p>
          </div>
        </div>
      </section>

      {/* Donabedian's Essence */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            ドナベディアンの本質
          </h2>

          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-10 rounded-2xl shadow-xl mb-12">
            <p className="text-2xl font-bold text-center mb-6">
              ドナベディアン・モデルが教えるのは
            </p>
            <p className="text-3xl font-bold text-center">
              良い結果は、良い過程が、<br />良い構造の上で起きる。
            </p>
            <p className="text-xl text-center mt-6 text-emerald-50">
              という因果関係です。
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              改善の順番は必ずこうなります
            </h3>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="bg-emerald-500 text-white px-8 py-4 rounded-xl font-bold text-xl">
                構造
              </div>
              <i className="ri-arrow-right-line text-3xl text-emerald-600"></i>
              <div className="bg-teal-500 text-white px-8 py-4 rounded-xl font-bold text-xl">
                過程
              </div>
              <i className="ri-arrow-right-line text-3xl text-teal-600"></i>
              <div className="bg-cyan-500 text-white px-8 py-4 rounded-xl font-bold text-xl">
                結果
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Layers Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            調剤薬局への翻訳｜3層をどう設計するか
          </h2>

          {/* Structure */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-6 rounded-t-2xl">
              <h3 className="text-3xl font-bold flex items-center gap-3">
                <i className="ri-building-4-line text-4xl"></i>
                ① Structure（構造）
              </h3>
              <p className="text-xl mt-2 text-emerald-50">再現性の「土台」</p>
            </div>
            <div className="bg-white p-8 rounded-b-2xl shadow-lg">
              <p className="text-lg text-gray-700 mb-6">
                ここが弱いと、どんなに優秀な薬剤師でも質は安定しません。
              </p>
              <h4 className="text-xl font-bold text-gray-900 mb-4">薬局での具体例</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-emerald-600 mt-1"></i>
                  <p className="text-gray-700">投薬に集中できる人員配置（ピークに厚く）</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-emerald-600 mt-1"></i>
                  <p className="text-gray-700">プライバシーが守られる指導スペース</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-emerald-600 mt-1"></i>
                  <p className="text-gray-700">説明資材・チェックリスト・記録テンプレ</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-emerald-600 mt-1"></i>
                  <p className="text-gray-700">欠品時の代替提案が標準化された在庫設計</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg">
                <p className="font-bold flex items-center gap-2">
                  <i className="ri-arrow-right-line text-xl"></i>
                  構造は「頑張らなくても守れる状態」か？が判断基準です。
                </p>
              </div>
            </div>
          </div>

          {/* Process */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 rounded-t-2xl">
              <h3 className="text-3xl font-bold flex items-center gap-3">
                <i className="ri-flow-chart text-4xl"></i>
                ② Process（過程）
              </h3>
              <p className="text-xl mt-2 text-teal-50">最重要レイヤー</p>
            </div>
            <div className="bg-white p-8 rounded-b-2xl shadow-lg">
              <p className="text-lg text-gray-700 mb-6">
                過程とは、<strong className="text-teal-600">「実際に、やったかどうか」</strong>です。
              </p>
              <h4 className="text-xl font-bold text-gray-900 mb-4">服薬指導の必須プロセス例</h4>
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700">併用薬・OTC・サプリの確認</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                  <div>
                    <p className="text-gray-700 font-medium mb-2">アドヒアランス阻害因子</p>
                    <p className="text-sm text-gray-600">（眠気／勤務／食事／嚥下／費用）</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700">副作用の早期サインと対処説明</p>
                </div>
                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mt-1"></i>
                  <p className="text-gray-700">次に取るべき行動の明確化</p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">疑義照会も"過程"</h4>
                <p className="text-gray-700 mb-2">実施率だけでなく</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-center gap-2 text-gray-700">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                    介入カテゴリ・臨床的妥当性を見る
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg">
                <p className="font-bold flex items-center gap-2">
                  <i className="ri-arrow-right-line text-xl"></i>
                  ここが属人化している薬局は、結果も必ず不安定になります。
                </p>
              </div>
            </div>
          </div>

          {/* Outcome */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-6 rounded-t-2xl">
              <h3 className="text-3xl font-bold flex items-center gap-3">
                <i className="ri-trophy-line text-4xl"></i>
                ③ Outcome（結果）
              </h3>
              <p className="text-xl mt-2 text-cyan-50">最後に見る指標</p>
            </div>
            <div className="bg-white p-8 rounded-b-2xl shadow-lg">
              <p className="text-lg text-gray-700 mb-6">
                結果は重要です。ただし、<strong className="text-cyan-600">最後に見ます</strong>。
              </p>
              <h4 className="text-xl font-bold text-gray-900 mb-4">代表的なアウトカム</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg">
                  <i className="ri-heart-pulse-line text-2xl text-cyan-600"></i>
                  <p className="text-gray-700">服薬継続率</p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg">
                  <i className="ri-medicine-bottle-line text-2xl text-cyan-600"></i>
                  <p className="text-gray-700">残薬減少</p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg">
                  <i className="ri-alert-line text-2xl text-cyan-600"></i>
                  <p className="text-gray-700">副作用の早期発見（受診勧奨）</p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg">
                  <i className="ri-star-line text-2xl text-cyan-600"></i>
                  <p className="text-gray-700">患者満足度（NPS等）</p>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg">
                <p className="font-bold flex items-center gap-2">
                  <i className="ri-arrow-right-line text-xl"></i>
                  結果が悪いときは、必ず過程か構造に戻る。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Connection with Other Theories */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            ドナベディアンと他理論の接続
          </h2>

          <p className="text-xl text-gray-700 text-center mb-12">
            このモデルは、これまでの理論と完全につながります。
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-gray-900 mb-2">TOC</h3>
              <p className="text-gray-700">→ 制約工程（投薬）を守る構造設計</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-xl border-l-4 border-cyan-500">
              <h3 className="text-lg font-bold text-gray-900 mb-2">リトルの法則</h3>
              <p className="text-gray-700">→ 待ち時間短縮＝過程の質向上</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-xl border-l-4 border-red-500">
              <h3 className="text-lg font-bold text-gray-900 mb-2">スイスチーズ</h3>
              <p className="text-gray-700">→ 指導プロセスを防御層にする</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border-l-4 border-amber-500">
              <h3 className="text-lg font-bold text-gray-900 mb-2">ピーク・エンド</h3>
              <p className="text-gray-700">→ 投薬の"エンド"を設計</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-8 rounded-2xl shadow-lg text-center">
            <p className="text-2xl font-bold flex items-center justify-center gap-3">
              <i className="ri-compass-3-line text-3xl"></i>
              ドナベディアンは「質の司令塔」です。
            </p>
          </div>
        </div>
      </section>

      {/* KPI Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
            KPI｜ドナベディアン視点で見る指標
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            結果だけを追わないために、次の順でKPIを置きます。
          </p>

          <div className="space-y-8">
            {/* Structure KPI */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-emerald-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <i className="ri-building-4-line text-xl text-emerald-600"></i>
                </div>
                構造KPI
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <i className="ri-time-line text-xl text-emerald-600"></i>
                  <span className="text-gray-700">投薬対応可能時間</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <i className="ri-team-line text-xl text-emerald-600"></i>
                  <span className="text-gray-700">人員配置適合率</span>
                </div>
              </div>
            </div>

            {/* Process KPI */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-teal-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-flow-chart text-xl text-teal-600"></i>
                </div>
                過程KPI
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                  <i className="ri-checkbox-line text-xl text-teal-600"></i>
                  <span className="text-gray-700">指導チェックリスト遵守率</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                  <i className="ri-question-line text-xl text-teal-600"></i>
                  <span className="text-gray-700">疑義照会の介入カテゴリ別件数</span>
                </div>
              </div>
            </div>

            {/* Outcome KPI */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-cyan-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <i className="ri-trophy-line text-xl text-cyan-600"></i>
                </div>
                結果KPI
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg">
                  <i className="ri-heart-pulse-line text-xl text-cyan-600"></i>
                  <span className="text-gray-700">継続率</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg">
                  <i className="ri-medicine-bottle-line text-xl text-cyan-600"></i>
                  <span className="text-gray-700">残薬調整件数</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="py-20 bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-8 text-center">
            明日の一手｜最初にやるべきこと
          </h2>
          <p className="text-xl text-center mb-12 text-emerald-50">
            明日やることは、これだけです。
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm p-10 rounded-2xl border-2 border-white/30 mb-12">
            <div className="flex items-start gap-4 mb-6">
              <i className="ri-arrow-right-circle-fill text-4xl text-white mt-1"></i>
              <p className="text-3xl font-bold">
                「服薬指導で必ず聞く3項目」を決める。
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl">
            <p className="text-lg mb-6">
              完璧なチェックリストはいりません。まずは3つ。
            </p>
            <p className="text-lg mb-4">それだけで、</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="text-lg">指導のばらつき</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="text-lg">見落とし</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span className="text-lg">属人化</span>
              </div>
            </div>
            <p className="text-lg mt-6 font-bold">
              が確実に減ります。
            </p>
          </div>
        </div>
      </section>

      {/* Related Theories */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            関連する理論
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/theory/toc" 
              className="group bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 border border-purple-100"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-focus-3-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">TOC</h3>
              <p className="text-gray-600">制約理論</p>
            </Link>
            <Link 
              to="/theory/littles-law" 
              className="group bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 border border-cyan-100"
            >
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-time-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">リトルの法則</h3>
              <p className="text-gray-600">待ち行列理論</p>
            </Link>
            <Link 
              to="/theory/swiss-cheese-model" 
              className="group bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 border border-red-100"
            >
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">スイスチーズ・モデル</h3>
              <p className="text-gray-600">医療安全理論</p>
            </Link>
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/theory" 
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium text-lg group"
            >
              <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
              理論一覧に戻る
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
